import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronDown } from "lucide-react";
import { cva } from "class-variance-authority";

const selectTriggerVariants = cva(
    "data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-fit items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    {
      variants: {
        variant: {
          "default": "border-input bg-transparent border-theme",
          "theme": "border-theme bg-theme-0 text-theme-0 font-theme",
        },
      },
      defaultVariants: {
        variant: "theme",
      },
    }
  )

export interface MultiselectOption {
  value: string;
  label: string;
}

interface MultiselectProps {
  options: MultiselectOption[];
  value: string[];
  onValueChange: (value: string[]) => void;
  placeholder?: string;
  triggerClassName?: string;
  contentClassName?: string;
  container?: HTMLElement | null;
  variant?: "default" | "theme";
}

export const Multiselect: React.FC<MultiselectProps> = ({
  options,
  value = [],
  onValueChange,
  placeholder = "Select options",
  triggerClassName = "",
  contentClassName = "w-[200px] p-0",
  container,
  variant = "theme"
}) => {
  const handleOptionClick = (optionValue: string) => {
    const currentValues = value || [];
    let newValues: string[];
    
    if (currentValues.includes(optionValue)) {
      newValues = currentValues.filter((v: string) => v !== optionValue);
    } else {
      newValues = [...currentValues, optionValue];
    }
    
    onValueChange(newValues);
  };

  const selectedCount = value?.length || 0;
  const displayText = selectedCount > 0 
    ? `${selectedCount} selected`
    : placeholder;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className={`${selectTriggerVariants({ variant })} ${triggerClassName}`}>
          <span className="text-base-0">{displayText}</span>
          <ChevronDown className="h-4 w-4 text-base-1" />
        </div>
      </PopoverTrigger>
      <PopoverContent className={contentClassName + " bg-theme-0 border-theme overflow-hidden text-theme-0"} container={container} variant={variant}>
        <div>
          {options.map((option) => (
            <div
              key={option.value}
              className="relative flex w-full cursor-pointer text-base-0 hover:bg-base-1 hover:text-base-0 select-none items-center border-base px-2 py-1.5 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
              onClick={() => handleOptionClick(option.value)}
            >
              <span className="flex-1">{option.label}</span>
              {value?.includes(option.value) && (
                <Check className="h-4 w-4" />
              )}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};